import {
  Clubhouse_LeagueScoring,
  Clubhouse_LeagueType,
  Clubhouse_LeagueV2Status,
  Clubhouse_LeagueWizardScheduleMode,
  Clubhouse_LgRoundStatusV2,
} from "./enums";

/**
 * Mirrors the backend `LeagueV2RosterChangePolicy`. The API is the authority —
 * every rule here is enforced server-side as well — but the operator should
 * never be offered a button that is guaranteed to fail.
 */

/** Exact copy shown wherever a started Match Play league blocks a roster change. */
export const MATCH_PLAY_LOCK_MESSAGE =
  "Full-time roster and teams are locked once Match Play begins. Use a substitute to cover an existing player.";

/** Shown when a finalized team league is asked to absorb a single player. */
export const TEAM_ADD_REQUIRES_COMPLETE_TEAM_MESSAGE =
  "Teams need two players, so a finalized team league grows by a whole team. Add both players together.";

/** Confirmation before withdrawing one player from a started individual league. */
export const WITHDRAW_PLAYER_CONFIRMATION =
  "Withdraw this player from future league play? Their completed and already-started rounds will remain part of the league history.";

/** Confirmation before withdrawing a complete team from a started team league. */
export const WITHDRAW_TEAM_CONFIRMATION =
  "Withdraw this team from future league play? Completed and already-started rounds will remain in league history.";

/** Why a started team league will not drop one full-time player. */
export const SINGLE_PLAYER_TEAM_WITHDRAWAL_MESSAGE =
  "A player cannot leave a started team league on their own, because their teammate would be left on a one-player team. Use a substitute to cover them, or withdraw the whole team from future play.";

/** What the roster row offers for a full-time player. */
export type FullTimeRosterRemoval = "remove" | "withdraw" | "none";

export type LeagueRosterChangeContext = {
  status?: Clubhouse_LeagueV2Status | null;
  type?: Clubhouse_LeagueType | null;
  scoring?: Clubhouse_LeagueScoring | null;
  scheduleMode?: Clubhouse_LeagueWizardScheduleMode | null;
  /**
   * Whether any Match round already holds real play state. Supplied by the API;
   * when it is unknown, a finalized Match league is treated as started so the UI
   * errs toward the lock rather than offering a path the backend will refuse.
   */
  matchPlayBegun?: boolean | null;
};

export const isFinalized = (ctx: LeagueRosterChangeContext) =>
  ctx.status === Clubhouse_LeagueV2Status.ACTIVE;

export const isMatchPlay = (ctx: LeagueRosterChangeContext) =>
  ctx.scoring === Clubhouse_LeagueScoring.MATCH;

export const isTeamFormat = (ctx: LeagueRosterChangeContext) =>
  ctx.type === Clubhouse_LeagueType.TEAM ||
  ctx.type === Clubhouse_LeagueType.SCRAMBLE;

/** True once a Match league holds real play state and its pairings are fixed. */
export const isMatchPlayLocked = (ctx: LeagueRosterChangeContext) =>
  isMatchPlay(ctx) && isFinalized(ctx) && ctx.matchPlayBegun !== false;

/**
 * Whether the full-time roster can still be changed at all. Stroke and Points
 * stay open to late entry in both schedule modes; Match Play is the only format
 * that locks.
 */
export const canMutateFullTimeRoster = (ctx: LeagueRosterChangeContext) =>
  !isMatchPlayLocked(ctx);

/**
 * Whether a lone full-time player may be added. A finalized team league cannot
 * absorb one player without going odd and leaving them unassigned, so it has to
 * grow by a complete team instead.
 */
export const canAddLoneFullTimePlayer = (ctx: LeagueRosterChangeContext) =>
  canMutateFullTimeRoster(ctx) && !(isFinalized(ctx) && isTeamFormat(ctx));

/**
 * Whether adding full-time players has to arrive as a whole team. True for a
 * finalized team league that is still open to roster change — the Add Team flow
 * is the normal way such a league grows.
 */
export const requiresCompleteTeamAdd = (ctx: LeagueRosterChangeContext) =>
  isFinalized(ctx) && isTeamFormat(ctx) && !isMatchPlayLocked(ctx);

/**
 * Whether granular team edits (create a team, delete one, assign or unassign a
 * player) are available.
 *
 * Open while the league is a draft, and — for Match leagues only — right up
 * until play begins, because a pre-start Match league regenerates its pairings
 * from whatever roster the edits leave behind. A finalized Stroke or Points team
 * league has no schedule to rebuild, so each step would just leave an invalid
 * team and it must use the complete-team flow instead.
 *
 * `matchPlayBegun === false` rather than a falsy check: "unknown" has to read as
 * locked, or a caller that never supplies it would quietly reopen a started
 * league's board.
 */
export const canEditTeamStructureStepByStep = (
  ctx: LeagueRosterChangeContext,
) => !isFinalized(ctx) || (isMatchPlay(ctx) && ctx.matchPlayBegun === false);

/**
 * Why a full-time roster add is unavailable, or `null` when it is allowed.
 * Match Play's lock is reported ahead of the team rule because it is the harder
 * stop: no roster change of any kind is possible.
 */
/**
 * How a full-time roster row removes someone. Before play begins the existing
 * Remove stays. After individual stroke or points has started it is Withdraw.
 * A started team league does not offer a one-player remove, and a started
 * Match league offers neither.
 */
export const fullTimeRosterRemoval = (
  ctx: LeagueRosterChangeContext,
): FullTimeRosterRemoval => {
  if (isMatchPlayLocked(ctx)) return "none";
  if (!isFinalized(ctx) || ctx.matchPlayBegun !== true) return "remove";
  if (isTeamFormat(ctx)) return "none";
  if (
    ctx.scoring === Clubhouse_LeagueScoring.STROKE ||
    ctx.scoring === Clubhouse_LeagueScoring.POINTS
  ) {
    return "withdraw";
  }
  return "none";
};

/** True when a started team stroke or points league may withdraw one whole team. */
export const canWithdrawTeam = (ctx: LeagueRosterChangeContext) =>
  isFinalized(ctx) &&
  ctx.matchPlayBegun === true &&
  isTeamFormat(ctx) &&
  !isMatchPlay(ctx);

export const describeFullTimeAddBlock = (
  ctx: LeagueRosterChangeContext,
): string | null => {
  if (isMatchPlayLocked(ctx)) return MATCH_PLAY_LOCK_MESSAGE;
  if (isFinalized(ctx) && isTeamFormat(ctx))
    return TEAM_ADD_REQUIRES_COMPLETE_TEAM_MESSAGE;
  return null;
};

/** A round the late-entry disclosure can see. Only number and status matter. */
export type LateEntryRound = {
  roundNumber?: number;
  status?: Clubhouse_LgRoundStatusV2 | string | null;
};

/**
 * True once Round 1 has closed: Completed or Finalized, which the app presents
 * as the same "Completed" state. A player may be added through the end of
 * Round 1 and not after it closes (CLUB-2352), so from then on the full-time
 * add and import paths are withdrawn for good. Substitutes are not covered.
 * Unknown rounds read as "not closed" so a loading league keeps its buttons.
 */
export const isRoundOneClosed = (
  rounds: readonly LateEntryRound[] | null | undefined,
): boolean =>
  (rounds ?? []).some(
    (round) =>
      round.roundNumber === 1 &&
      (round.status === Clubhouse_LgRoundStatusV2.Completed ||
        round.status === Clubhouse_LgRoundStatusV2.Finalized),
  );

/**
 * The lowest-numbered round that is still open for play. Sending players to a
 * simulator moves that round to InProgress, and it is still the open round:
 * a player added now is written onto it and has to finish it.
 */
export const currentOpenRoundNumber = (
  rounds: readonly LateEntryRound[] | null | undefined,
): number | null => {
  let open: number | null = null;
  for (const round of rounds ?? []) {
    if (
      round.roundNumber == null ||
      (round.status !== Clubhouse_LgRoundStatusV2.Open &&
        round.status !== Clubhouse_LgRoundStatusV2.InProgress)
    ) {
      continue;
    }
    if (open == null || round.roundNumber < open) open = round.roundNumber;
  }
  return open;
};

/**
 * The sentence shown before a Weekly/Fixed add commits, and only while a round
 * is actually open. Season Flex is a different rule, and a finished current
 * round must not be described as something the new player still has to complete.
 */
export const describeOpenRoundCompletionNotice = (
  ctx: LeagueRosterChangeContext,
  rounds: readonly LateEntryRound[] | null | undefined,
): string | null => {
  if (!isFinalized(ctx) || !canMutateFullTimeRoster(ctx)) return null;
  if (ctx.scheduleMode === Clubhouse_LeagueWizardScheduleMode.SeasonFlex) {
    return null;
  }
  const roundNumber = currentOpenRoundNumber(rounds);
  if (roundNumber == null) return null;
  const who = isTeamFormat(ctx) ? "A team" : "A player";
  return (
    `Round ${roundNumber} is still open. ${who} added now becomes part of that round's completion requirement, ` +
    "plus every round after it. Finished rounds are left alone."
  );
};

/**
 * Which rounds a late entrant will play, for the disclosure shown before the
 * add. Season Flex progression is per player, so a late entrant gets the whole
 * season. Weekly rounds are calendar-anchored: when a round is open they join
 * it, and when it is already finished they join only the rounds after it.
 *
 * `rounds` omitted means the open round is not known yet, so Weekly keeps the
 * general warning rather than claiming the current round is already finished.
 */
export const describeLateEntryRoundScope = (
  ctx: LeagueRosterChangeContext,
  rounds?: readonly LateEntryRound[] | null,
): string | null => {
  if (!isFinalized(ctx) || !canMutateFullTimeRoster(ctx)) return null;
  if (ctx.scheduleMode === Clubhouse_LeagueWizardScheduleMode.SeasonFlex) {
    return "This league has already started. A player added now plays every round of the season, including rounds other players have already finished.";
  }
  if (rounds == null) {
    return "This league has already started. A player added now joins the round that is open now — which makes them part of its completion — plus every round after it. Finished rounds are left alone.";
  }
  return (
    describeOpenRoundCompletionNotice(ctx, rounds) ??
    "This league has already started. A player added now joins upcoming rounds only. Finished rounds are left alone."
  );
};
